import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { PaymentTypeIds } from '@getgreenline/homi-shared'
import { browserHistory } from 'react-router'
import { Alert } from 'antd-v4'
import { Routes } from '../../../../../../constants/Routes'

import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'

import { LoadingAnimation } from '../../../../../../components/LoadingAnimation'
import { PaymentTypeAccountSelector } from './PaymentTypeAccountSelector'
import { LocationDepartmentSelector } from './LocationDepartmentSelector'
import { TaxAccountSelector } from './TaxAccountSelector'
import { FormError } from '../../../../../../components/FormError'
import { SaveButton } from '../../../../../../components/Button/SaveButton'

import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { QuickBooksApi, QuickBooksModels, QuickBooksContracts } from '@getgreenline/quickbooks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
}

interface States {
  isLoading: boolean
  isSubmitting: boolean
  quickBooksAccounts: QuickBooksModels.IQuickBooksOnlineAccountInfo[]
  quickBooksLocations: QuickBooksModels.IQuickBooksOnlineClassInfo[]
  paymentTypesToSync: QuickBooksModels.IQuickBooksPaymentTypesToSync[]
  locationsToSync: QuickBooksModels.IQuickBooksLocationToSyncResponse[]
  taxesToSync: QuickBooksModels.IQuickBooksTaxToSyncResponse[]
  errorMessage: string
  networkError: string
}

@inject('currentCompanyStore')
@observer
export class QuickBooksSettings extends React.Component<Props, States> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: true,
      isSubmitting: false,
      quickBooksAccounts: [],
      quickBooksLocations: [],
      paymentTypesToSync: [],
      locationsToSync: [],
      taxesToSync: [],
      errorMessage: '',
      networkError: '',
    }
  }

  async componentDidMount() {
    const companyId = this.props.currentCompanyStore!.company!.id

    try {
      const { paymentTypesToSync, locationsToSync, taxesToSync } =
        await QuickBooksApi.getQuickBooksSyncAccounts(companyId)

      const { accountsInfo, classesInfo } = await QuickBooksApi.getQuickBooksAccounts(companyId)

      this.setState({
        isLoading: false,
        quickBooksAccounts: accountsInfo,
        quickBooksLocations: classesInfo,
        locationsToSync,
        taxesToSync,
        paymentTypesToSync,
      })
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        this.setState({
          ...this.state,
          networkError: error.response.data.message,
          isLoading: false,
        })

        return
      }

      await this.props.currentCompanyStore!.getById(companyId)
      browserHistory.push(Routes.DASHBOARD_INTEGRATIONS_QUICKBOOKS_AUTH_CONNECT(companyId))
    }
  }

  onPaymentTypeSelect = (
    paymentTypeId: PaymentTypeIds,
    debitAccountId: string | null,
    creditAccountId: string | null,
  ) => {
    const { paymentTypesToSync } = this.state
    const paymentTypes = paymentTypesToSync.map((paymentTypeToSync) => {
      if (paymentTypeToSync.paymentTypeId === paymentTypeId) {
        return {
          paymentTypeId: paymentTypeToSync.paymentTypeId,
          debitAccountId,
          creditAccountId,
        }
      } else {
        return paymentTypeToSync
      }
    })

    this.setState({
      paymentTypesToSync: paymentTypes,
    })
  }

  onLocationSelect = (locationId: number, classId: string | null) => {
    const { locationsToSync } = this.state
    const locations = locationsToSync.map((locationToSync) => {
      if (locationToSync.locationId === locationId) {
        return {
          locationId: locationToSync.locationId,
          locationName: locationToSync.locationName,
          classId: classId,
        }
      } else {
        return locationToSync
      }
    })
    this.setState({
      locationsToSync: locations,
    })
  }

  onTaxSelect = (taxId: number, creditAccountId: string | null) => {
    const { taxesToSync } = this.state
    const taxes = taxesToSync.map((taxToSync) => {
      if (taxToSync.taxId === taxId) {
        return {
          taxId: taxToSync.taxId,
          taxName: taxToSync.taxName,
          creditAccountId,
        }
      } else {
        return taxToSync
      }
    })

    this.setState({
      taxesToSync: taxes,
    })
  }

  syncAccounts = async () => {
    this.setState({ isSubmitting: true, errorMessage: '' })
    const { paymentTypesToSync, locationsToSync, taxesToSync } = this.state
    try {
      const quickBooksAccountPayload: QuickBooksContracts.IQuickBooksSyncAccountsRequest = {
        paymentTypesToSync: paymentTypesToSync,
        locationsToSync: locationsToSync.map((location) => ({
          locationId: location.locationId,
          classId: location.classId,
        })),
        taxesToSync: taxesToSync.map((tax) => ({
          taxId: tax.taxId,
          creditAccountId: tax.creditAccountId,
        })),
      }
      await QuickBooksApi.updateQuickBooksSyncAccounts(
        this.props.currentCompanyStore!.company!.id,
        quickBooksAccountPayload,
      )
    } catch (error) {
      this.setState({
        errorMessage: parseErrorMsg(error) || 'There was an error syncing accounts',
      })
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  render() {
    const {
      isLoading,
      isSubmitting,
      quickBooksAccounts,
      quickBooksLocations,
      paymentTypesToSync,
      locationsToSync,
      taxesToSync,
      errorMessage,
      networkError,
    } = this.state

    const quickBooksAccountOptions: Array<{ value: string; label: string }> =
      quickBooksAccounts.map((account) => ({
        value: account.id,
        label: account.name,
      }))
    const quickBooksLocationOptions: Array<{ value: string; label: string }> =
      quickBooksLocations.map((quickBooksClass) => ({
        value: quickBooksClass.id,
        label: quickBooksClass.name,
      }))

    if (isLoading) {
      return (
        <div>
          <LoadingAnimation />
        </div>
      )
    }

    if (networkError) {
      return (
        <div>
          <Alert
            style={{ marginTop: '2em' }}
            message={networkError}
            type='error'
            showIcon
            onClose={() =>
              this.setState({
                ...this.state,
                networkError: '',
              })
            }
          />
        </div>
      )
    }

    return (
      <div className='d-flex flex-wrap'>
        <div
          style={{
            width: '60em',
            padding: '1em',
            backgroundColor: 'white',
            marginRight: '3em',
            marginBottom: '3em',
          }}
        >
          <h4 className='mb-3'>Payment mapping</h4>
          <table className='table table-sm mb-0'>
            <thead className='thead-default'>
              <tr>
                <th>Type</th>
                <th>Debit account</th>
                <th>Credit account</th>
              </tr>
            </thead>
            <tbody>
              {paymentTypesToSync.map((p) => (
                <PaymentTypeAccountSelector
                  key={p.paymentTypeId}
                  paymentType={p}
                  options={quickBooksAccountOptions}
                  onSave={this.onPaymentTypeSelect}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            width: '40em',
            padding: '1em',
            backgroundColor: 'white',
            marginRight: '3em',
            marginBottom: '3em',
          }}
        >
          <h4>Location mapping</h4>
          <p className='text-muted'>
            Locations that are not synced will not show up in QuickBooks journal entries
          </p>
          <table className='table table-sm mb-0'>
            <thead className='thead-default'>
              <tr>
                <th>Location</th>
                <th>Class</th>
              </tr>
            </thead>
            <tbody>
              {locationsToSync.map((l) => (
                <LocationDepartmentSelector
                  key={l.locationId}
                  location={l}
                  options={quickBooksLocationOptions}
                  onSelect={this.onLocationSelect}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* <div style={{width: '40em', padding: '1em', backgroundColor: 'white', marginRight: '3em'}}>
          <h4>Income mapping</h4>
          <p className='text-muted'>Income mapping is required to generate journal entries</p>
          <table className='table table-sm mb-0'>
            <thead className='thead-default'>
              <tr>
                <th>Income</th>
                <th>QBO Credit account</th>
              </tr>
            </thead>
            <tbody>
              <IncomeAccountSelector
                income={incomeToSync}
                options={quickBooksAccountOptions}
                onSelect={this.onIncomeSelect}
              />
            </tbody>
          </table>
        </div> */}

        <div
          style={{ width: '40em', padding: '1em', backgroundColor: 'white', marginRight: '3em' }}
        >
          <h4 className='mb-3'>Tax mapping</h4>
          <table className='table table-sm mb-0'>
            <thead className='thead-default'>
              <tr>
                <th>Type</th>
                <th>Credit account</th>
              </tr>
            </thead>
            <tbody>
              {taxesToSync.map((t) => (
                <TaxAccountSelector
                  key={t.taxId}
                  tax={t}
                  options={quickBooksAccountOptions}
                  onSelect={this.onTaxSelect}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className='w-100 mt-3'>
          <div style={{ width: '10em' }}>
            <SaveButton title='Save' isSaving={isSubmitting} onClick={this.syncAccounts} />
          </div>
        </div>

        <div className='mt-3' style={{ minWidth: '40em' }}>
          <FormError message={errorMessage} />
        </div>
      </div>
    )
  }
}
